import { Building2, Info, LoaderCircle, Network, Plus, RefreshCw, UserRoundCog } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  apiClient,
  type AccessCompany,
  type AccessUser,
  type AccessUserDetail,
  type CompanyGroup,
  type LoginMethod
} from "@/shared/api/api-client";
import { errorMessage } from "@/shared/api/http";
import { ListSearch } from "@/shared/components/ListSearch";
import { ROLE_LABEL, ROLES } from "@/shared/utils/roles";
import { AccessLinkList, inactiveGroupReason } from "./AccessLinkList";
import { AdminPageHeader } from "@/features/admin/AdminPageHeader";
import { Select } from "@/shared/components/Select";
import { filterBySearch } from "@/shared/utils/text-search";
import { welcomeEmailNotice } from "@/shared/vocabulary/welcome-email";
import { LOGIN_METHOD_LABEL, STATUS_LABEL, STATUS_TONE, UserDetailPanel, type ActOptions } from "./UserDetailPanel";
import { useRevealDetail } from "./use-reveal-detail";
import { RoleCapabilitiesDialog } from "./RoleCapabilitiesDialog";

/**
 * `loginMode`: o login por pessoa da instalação (`"google"`), ou nulo com
 * o modo desligado (2026-10-07). Ligado, quem é Conta Google não recebe
 * o convite do Keycloak na criação — entra com a conta do e-mail
 * cadastrado —, mas recebe o e-mail de boas-vindas, com o endereço do site
 * e o cargo (2026-10-27); a tela diz a quem convidou se ele saiu. E o
 * Máster pode criar a exceção "usuário e senha". O servidor é quem
 * decide (ele recusa a exceção vinda de Administrador); isto só ajusta o
 * que a tela oferece e diz.
 *
 * `master`: quem olha é o Máster (vê e administra todo mundo). Senão é o
 * Administrador, que só vê e administra quem é do núcleo dele, e só nos
 * cargos abaixo do dele (2026-10-22, proposta de IAM).
 *
 * **O acesso entra no convite** (2026-10-22, RN-02/04): núcleo para o
 * Administrador, empresas para os demais; o Máster não leva vínculo.
 * Núcleo para os demais, se o Máster quiser, se dá depois no detalhe.
 *
 * Reenviar convite (2026-10-14) vale pra todo cadastro aguardando o
 * primeiro acesso, e pro Administrador também: a conta Google recebe o
 * e-mail de confirmar o endereço, que leva ao site, e de novo o de
 * boas-vindas; a exceção por senha, o de definir senha.
 *
 * Suspensão (RN-11/12): pausa o acesso guardando cargo e vínculos.
 * Suspender à mão é só por afastamento; a de inatividade vem da rotina do
 * worker. O Máster e o Administrador do núcleo da pessoa reativam.
 */
export function UsersAdmin({
  token,
  master,
  loginMode = null
}: {
  token: string;
  master: boolean;
  loginMode?: string | null;
}) {
  const signsInWithProvider = (user: Pick<AccessUser, "login_method">) =>
    Boolean(loginMode) && user.login_method === "google";
  const statusLabel = (user: AccessUser) =>
    user.status === "pending" && signsInWithProvider(user)
      ? "Aguardando primeiro acesso"
      : STATUS_LABEL[user.status];
  const [users, setUsers] = useState<AccessUser[]>([]);
  const [selected, setSelected] = useState<AccessUserDetail | null>(null);
  const [companies, setCompanies] = useState<AccessCompany[]>([]);
  const [groups, setGroups] = useState<CompanyGroup[]>([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<AccessUser["role"]>("specialist");
  const [showRoleCapabilities, setShowRoleCapabilities] = useState(false);
  const [loginMethod, setLoginMethod] = useState<LoginMethod>("google");
  // O acesso com que a conta nasce, escolhido antes de criar.
  const [newGroups, setNewGroups] = useState<CompanyGroup[]>([]);
  const [newCompanies, setNewCompanies] = useState<AccessCompany[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Confirmação de uma ação que não muda nada na tela (o reenvio do
  // convite): sem ela, o clique parecia não ter feito nada.
  const [notice, setNotice] = useState<string | null>(null);
  // Inativo é conta encerrada: fica fora da lista por padrão (pedido de
  // 2026-10-14), a um clique de voltar — reativar continua possível.
  const [showInactive, setShowInactive] = useState(false);
  const [search, setSearch] = useState("");
  const detailCache = useRef(new Map<number, AccessUserDetail>());
  const selectionSequence = useRef(0);
  const detailCard = useRef<HTMLDivElement>(null);
  useRevealDetail(detailCard, selected?.id);

  const load = useCallback(async (selectedId?: number) => {
    setBusy(true);
    try {
      const [nextUsers, nextCompanies, nextGroups, nextSelected] = await Promise.all([
        apiClient.listAccessUsers(token),
        apiClient.listAccessCompanies(token),
        apiClient.listCompanyGroups(token),
        selectedId === undefined
          ? Promise.resolve(null)
          : apiClient.accessUser(token, selectedId)
      ]);
      setUsers(nextUsers);
      setCompanies(nextCompanies);
      // Todos, ativos e inativos (2026-10-23, pedido da dona): a lista mostra
      // a situação numa bolinha, e o inativo aparece sem poder ser escolhido.
      setGroups(nextGroups);
      setError(null);
      if (nextSelected) {
        detailCache.current.set(nextSelected.id, nextSelected);
        // O painel de detalhe é remontado por usuário (`key`) e segue o
        // cargo e o nome do `selected`: abrir o recém-criado (`create` →
        // `load`) não herda mais o cargo do usuário ANTERIOR com "Alterar
        // cargo" aceso (bug de 2026-10-09, teste de render).
        setSelected(nextSelected);
      }
    } catch (cause) {
      setError(errorMessage(cause, "Não foi possível carregar os usuários."));
    } finally {
      setBusy(false);
    }
  }, [token]);

  useEffect(() => { void load(); }, [load]);

  /** Roda uma escrita e recarrega; devolve se deu certo (o diálogo de
   *  confirmação só fecha no sucesso — na recusa, o erro aparece com ele aberto).
   *  `reload: false` é pra ação que não muda nada na tela (mandar e-mail de
   *  senha): recarregar seriam quatro chamadas pra mostrar o mesmo. */
  const act = async <T,>(
    operation: () => Promise<T>,
    done?: string | ((result: T) => string),
    { reload = true }: ActOptions = {}
  ): Promise<boolean> => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await operation();
      if (done) setNotice(typeof done === "function" ? done(result) : done);
      if (reload) await load(selected?.id);
      else setBusy(false);
      return true;
    } catch (cause) {
      setError(errorMessage(cause, "Não foi possível concluir a operação."));
      setBusy(false);
      return false;
    }
  };

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const created = await apiClient.createAccessUser(token, {
        name,
        email,
        role,
        ...(loginMode ? { login_method: loginMethod } : {}),
        group_ids: scope.groups ? newGroups.map(group => group.id) : [],
        company_ids: scope.companies ? newCompanies.map(company => company.id) : []
      });
      setName("");
      setEmail("");
      setNewGroups([]);
      setNewCompanies([]);
      await load(created.id);
      // Depois do `load`, que não mexe no aviso. Falhou ou não está
      // configurado: vira erro, porque a pessoa não foi avisada e quem
      // convidou precisa agir. A conta foi criada assim mesmo.
      const welcome = created.welcome_email;
      if (welcome === "failed" || welcome === "unavailable") {
        setError(`Acesso criado. ${welcomeEmailNotice(welcome, created.email)}`);
      } else if (welcome) {
        setNotice(`Acesso criado. ${welcomeEmailNotice(welcome, created.email)}`);
      }
    } catch (cause) {
      setError(errorMessage(cause, "Não foi possível concluir a operação."));
      setBusy(false);
    }
  };

  const select = async (user: AccessUser) => {
    const sequence = ++selectionSequence.current;
    const cached = detailCache.current.get(user.id);
    setNotice(null);
    if (cached) {
      setSelected(cached);
      setError(null);
      return;
    }
    setBusy(true);
    try {
      const detail = await apiClient.accessUser(token, user.id);
      if (sequence !== selectionSequence.current) return;
      detailCache.current.set(detail.id, detail);
      setSelected(detail);
      setError(null);
    } catch (cause) {
      if (sequence === selectionSequence.current) {
        setError(errorMessage(cause, "Não foi possível abrir o usuário."));
      }
    } finally {
      if (sequence === selectionSequence.current) setBusy(false);
    }
  };

  const refresh = () => {
    detailCache.current.clear();
    void load(selected?.id);
  };

  const inactiveCount = users.filter(user => user.status === "disabled").length;
  const statusFiltered = showInactive ? users : users.filter(user => user.status !== "disabled");
  const visibleUsers = filterBySearch(statusFiltered, search, user => [user.name, user.email, ROLE_LABEL[user.role]]);

  const allowedRoles: AccessUser["role"][] = master
    ? [...ROLES]
    : ["specialist", "technician", "viewer"];

  // Que vínculo o convite leva. Desde 2026-10-23 (pedido da dona) é
  // empresa OU núcleo: o Máster escolhe núcleos e/ou empresas pra qualquer
  // cargo abaixo do dele, e o Administrador precisa de pelo menos um
  // núcleo. Quem não é Máster dá só empresa (RN-05: núcleo é do Máster). O
  // servidor confere de novo (`_require_initial_scope`); isto só decide o
  // que a tela oferece.
  const scope = {
    groups: master && role !== "master",
    companies: role !== "master"
  };
  const scopeReady =
    role === "master" ||
    (role === "administrator" ? newGroups.length > 0 : newGroups.length + newCompanies.length > 0);

  return (
    <section className="copiloto-admin access-admin ajustes-panel admin-panel">
      <AdminPageHeader
        title="Usuários"
        description="Contas locais do ReputAItion e seus acessos por grupo ou empresa."
        actions={<button className="secondary-button access-refresh-button" onClick={refresh} disabled={busy}><RefreshCw size={16} /> Atualizar</button>}
      />
      {error && <p className="form-error-alert" role="alert">{error}</p>}
      {notice && <p className="access-notice" role="status">{notice}</p>}

      <div className="card access-create-card">
        <div className="card-title"><div><Plus size={18} aria-hidden="true" /><h3>Criar usuário</h3><p>{master
  ? "Nome, e-mail, cargo e o acesso com que a pessoa entra: núcleos, empresas ou os dois (o Administrador precisa de pelo menos um núcleo). O Máster enxerga tudo e não leva vínculo."
  : "Nome, e-mail, cargo e as empresas que a pessoa vai acessar, dos seus núcleos."}</p></div></div>
        <div className="access-form-grid access-user-form">
          <label className="field"><span>Nome</span><input placeholder="Nome do usuário" value={name} onChange={event => setName(event.target.value)} /></label>
          <label className="field"><span>E-mail</span><input type="email" placeholder="email@exemplo.com" value={email} onChange={event => setEmail(event.target.value)} /></label>
          <label className="field"><span>Cargo</span><Select value={role} onChange={event => setRole(event.target.value as AccessUser["role"])}>{allowedRoles.map(item => <option key={item} value={item}>{ROLE_LABEL[item]}</option>)}</Select></label>
          {loginMode && master && <label className="field"><span>Entra com</span><Select value={loginMethod} onChange={event => setLoginMethod(event.target.value as LoginMethod)}><option value="google">{LOGIN_METHOD_LABEL.google}</option><option value="password">{LOGIN_METHOD_LABEL.password} (exceção)</option></Select></label>}
          <button className="primary-button access-create-button" disabled={busy || !name.trim() || !email.trim() || !scopeReady} onClick={() => void create()}>{busy ? <LoaderCircle className="spin" size={16} /> : <Plus size={16} />} {signsInWithProvider({ login_method: loginMethod }) ? "Criar acesso" : "Criar e enviar convite"}</button>
        </div>
        <button type="button" className="text-button access-roles-help" onClick={() => setShowRoleCapabilities(true)}>
          <Info size={14} aria-hidden="true"/> O que cada cargo pode fazer
        </button>
        {showRoleCapabilities && <RoleCapabilitiesDialog selected={role} selectable={allowedRoles} onSelect={setRole} onClose={() => setShowRoleCapabilities(false)}/>}
        {(scope.groups || scope.companies) && (
          <div className="access-create-scope">
            {scope.groups && (
              <AccessLinkList
                title={role === "administrator" ? "Núcleos do novo Administrador" : "Núcleos que a pessoa vai acessar"}
                hint={role === "administrator"
                  ? "Pelo menos um. Ele enxerga e administra as empresas de cada núcleo."
                  : "Enxerga todas as empresas de cada núcleo. Núcleo ou empresa: pelo menos um dos dois."}
                searchPlaceholder="Digite o nome do núcleo"
                detail={item => item.description}
                icon={<Network size={14} />}
                isActive={item => item.is_active}
                statusLabels={["Ativo", "Inativo"]}
                unavailable={inactiveGroupReason}
                available={groups.filter(group => !newGroups.some(chosen => chosen.id === group.id))}
                linked={newGroups}
                label={item => item.name}
                onGrant={async id => setNewGroups(current => [...current, ...groups.filter(group => group.id === id)])}
                onRevoke={async id => setNewGroups(current => current.filter(group => group.id !== id))}
              />
            )}
            {scope.companies && (
              <AccessLinkList
                title="Empresas que a pessoa vai acessar"
                hint={!master
                  ? "Pelo menos uma, dos seus núcleos."
                  : role === "administrator"
                    ? "Opcional: empresas além das dos núcleos."
                    : "Núcleo ou empresa: pelo menos um dos dois."}
                searchPlaceholder="Digite o nome da empresa"
                icon={<Building2 size={14} />}
                isActive={item => item.is_active}
                available={companies.filter(company => !newCompanies.some(chosen => chosen.id === company.id))}
                linked={newCompanies}
                label={item => item.name}
                onGrant={async id => setNewCompanies(current => [...current, ...companies.filter(company => company.id === id)])}
                onRevoke={async id => setNewCompanies(current => current.filter(company => company.id !== id))}
              />
            )}
          </div>
        )}
        {signsInWithProvider({ login_method: loginMethod }) && <p className="ai-admin-caption">Não há convite por e-mail: a pessoa entra no ReputAItion com a conta Google do e-mail informado.</p>}
        {loginMode && loginMethod === "password" && <p className="ai-admin-caption">Exceção para quem não tem conta Google da empresa: a pessoa recebe o e-mail para definir a senha, e a exceção vale 90 dias (depois o acesso é suspenso até um Máster renovar).</p>}
      </div>

      <div className="access-split">
        <div className="card access-list-card">
          <div className="access-list-toolbar">
            <ListSearch
              value={search}
              onChange={setSearch}
              placeholder="Buscar por nome, e-mail ou cargo"
              shown={visibleUsers.length}
              total={statusFiltered.length}
            />
            <div className="access-list-toolbar-row">
              <span className="ai-admin-caption">
                {statusFiltered.length === users.length
                  ? `${users.length} ${users.length === 1 ? "usuário" : "usuários"}`
                  : `${statusFiltered.length} de ${users.length} usuários`}
              </span>
              <label className="admin-switch">
                <input type="checkbox" checked={showInactive} onChange={event => setShowInactive(event.target.checked)} />
                <span>Mostrar inativos{inactiveCount > 0 ? ` (${inactiveCount})` : ""}</span>
              </label>
            </div>
          </div>
          <div className="access-list-scroll">
          <table className="ai-admin-table"><thead><tr><th>Usuário</th><th>Cargo</th><th>Situação</th></tr></thead><tbody>
            {visibleUsers.map(user => (
              <tr
                key={user.id}
                tabIndex={0}
                aria-selected={selected?.id === user.id}
                className={`${user.status === "disabled" ? "row-inactive" : ""} ${selected?.id === user.id ? "is-selected" : ""}`}
                onClick={() => void select(user)}
                onKeyDown={event => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    void select(user);
                  }
                }}
              >
                <td><strong>{user.name}</strong><span className="ai-admin-caption">{user.email}</span></td>
                <td>{ROLE_LABEL[user.role]}</td>
                <td><span className={`badge-pill ${STATUS_TONE[user.status]}`}>{statusLabel(user)}</span></td>
              </tr>
            ))}
          </tbody></table>
          {!busy && users.length === 0 && <p className="access-empty">Nenhum usuário disponível.</p>}
          {!busy && users.length > 0 && statusFiltered.length === 0 && <p className="access-empty">Todos os usuários estão inativos. Marque “Mostrar inativos” para vê-los.</p>}
          </div>
        </div>

        <div className="card access-detail-card" ref={detailCard}>
          {!selected ? (
            <div className="access-empty">
              <UserRoundCog size={30} />
              <p>Selecione um usuário para administrar os vínculos.</p>
            </div>
          ) : (
            <UserDetailPanel
              key={selected.id}
              token={token}
              user={selected}
              master={master}
              loginMode={loginMode}
              busy={busy}
              groups={groups}
              companies={companies}
              allowedRoles={allowedRoles}
              statusLabel={statusLabel}
              act={act}
            />
          )}
        </div>
      </div>
    </section>
  );
}
